import React, { useEffect, useState, useRef } from "react";
import { DateRangePicker } from "react-date-range";
import { formatDMY, PrimaryColor } from "../common";
import { formatDateString } from "../pages/Dashboard/Auction/Auction";
import { enGB } from "date-fns/locale";
import ReactDOM from "react-dom";

interface DynamicDatePickerProps {
  setDate: (dates: { startDate: Date; endDate: Date; key: string }[]) => void;
  defaultDates?: { startDate: Date; endDate: Date; key: string };
  minDate?: string;
  maxDate?: string;
  singleDate?: boolean;
  selectedDates?:
    | { startDate: Date; endDate: Date; key: string }
    | Array<{ value: string; startDate?: Date; endDate?: Date }>;
  buttonClassName?: string;
  disabled?: boolean;
}

// Format date with ordinal suffix (e.g., "23rd Feb 2025")
const formatDateWithOrdinal = (date: Date | undefined): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }

  const day = date.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Determine ordinal suffix
  let suffix = "th";
  if (day === 1 || day === 21 || day === 31) {
    suffix = "st";
  } else if (day === 2 || day === 22) {
    suffix = "nd";
  } else if (day === 3 || day === 23) {
    suffix = "rd";
  }

  return `${day}${suffix} ${month} ${year}`;
};

export const DynamicDatePicker: React.FC<DynamicDatePickerProps> = ({
  setDate,
  defaultDates,
  selectedDates = [],
  minDate = "2021-01-01",
  maxDate = "2028-12-31",
  singleDate = false,
  buttonClassName,
  disabled = false,
}) => {
  // `mounted` controls DOM presence; `isOpen` drives the CSS enter/leave classes
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  const [tempDateRange, setTempDateRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  } | null>(null);

  const [showLongRangeConfirm, setShowLongRangeConfirm] = useState(false);
  const LONG_RANGE_DAYS = 31;

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (defaultDates) {
      const normalizedDefault = singleDate
        ? { ...defaultDates, endDate: defaultDates.startDate }
        : defaultDates;
      setDateRange(normalizedDefault);
    }
  }, [defaultDates, singleDate]);

  const openPicker = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setTempDateRange(
      singleDate ? { ...dateRange, endDate: dateRange.startDate } : dateRange,
    );
    setMounted(true);
    // Defer so the element is in the DOM before we trigger the transition
    requestAnimationFrame(() => setIsOpen(true));
  };

  const closePicker = () => {
    setIsOpen(false);
    // Wait for the CSS transition to finish before unmounting
    closeTimeoutRef.current = setTimeout(() => setMounted(false), 250);
  };

  const handleDateChange = (
    item:
      | Record<string, { startDate?: Date; endDate?: Date; key?: string }>
      | undefined,
  ) => {
    const selection =
      item?.selection ??
      Object.values(item ?? {}).find(
        (range) =>
          Boolean(range) &&
          (range?.startDate instanceof Date || range?.endDate instanceof Date),
      );

    setTempDateRange((prev) => {
      const fallbackStart =
        prev?.startDate ?? dateRange.startDate ?? new Date();
      const startDate =
        selection?.startDate instanceof Date
          ? selection.startDate
          : fallbackStart;
      const endDate =
        selection?.endDate instanceof Date ? selection.endDate : startDate;

      return {
        startDate,
        endDate: singleDate ? startDate : endDate,
        key: selection?.key || "selection",
      };
    });
  };

  const commitRange = (range: {
    startDate: Date;
    endDate: Date;
    key: string;
  }) => {
    const nextRange = singleDate
      ? { ...range, endDate: range.startDate }
      : range;
    const formattedStartDate = nextRange.startDate.toLocaleDateString("en-GB");
    const formattedEndDate = nextRange.endDate.toLocaleDateString("en-GB");

    const normalizedDefault = defaultDates
      ? singleDate
        ? { ...defaultDates, endDate: defaultDates.startDate }
        : defaultDates
      : undefined;

    const matchesDefault = normalizedDefault
      ? normalizedDefault.startDate.toLocaleDateString("en-GB") ===
          formattedStartDate &&
        normalizedDefault.endDate.toLocaleDateString("en-GB") ===
          formattedEndDate
      : false;

    setDateRange(nextRange);

    if (!matchesDefault) {
      setDate([
        {
          startDate: new Date(
            formattedStartDate.split("/").reverse().join("-"),
          ),
          endDate: new Date(formattedEndDate.split("/").reverse().join("-")),
          key: "selection",
        },
      ]);
    }
  };

  const handleSubmit = () => {
    if (tempDateRange) {
      if (!singleDate) {
        const diffDays =
          (tempDateRange.endDate.getTime() -
            tempDateRange.startDate.getTime()) /
          (1000 * 60 * 60 * 24);
        if (diffDays > LONG_RANGE_DAYS) {
          setShowLongRangeConfirm(true);
          return;
        }
      }
      commitRange(tempDateRange);
    }
    closePicker();
  };

  const handleConfirmLongRange = () => {
    if (tempDateRange) commitRange(tempDateRange);
    setShowLongRangeConfirm(false);
    closePicker();
  };

  const handleCancelLongRange = () => {
    setShowLongRangeConfirm(false);
  };

  // Compute all disabled dates
  const disabledDates: Date[] = [];

  if (typeof selectedDates === "object" && !Array.isArray(selectedDates)) {
    let current = new Date(selectedDates.startDate);
    const end = new Date(selectedDates.endDate);
    while (current <= end) {
      disabledDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  } else if (Array.isArray(selectedDates)) {
    selectedDates.forEach((range) => {
      const date = String(range.value).slice(0, 21).split(",");
      let current = new Date(date[0]);
      const end = new Date(date[1]);
      while (current <= end) {
        disabledDates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
    });
  }

  // Close picker when clicking outside
  useEffect(() => {
    if (!mounted) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (!target.isConnected) return;
      if (showLongRangeConfirm) return;
      if (buttonRef.current?.contains(target)) return;

      const datePickerElements = document.querySelectorAll(
        ".date-selection-wrapper, .rdrDefinedRangesWrapper",
      );
      let clickedInside = false;

      datePickerElements.forEach((el) => {
        if (el.contains(target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        closePicker();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mounted, showLongRangeConfirm]);

  const renderDatePickerPortal = () => {
    if (!mounted) return null;

    return ReactDOM.createPortal(
      <>
        <style>{`
          .dp-backdrop {
            transition: opacity 250ms ease;
          }
          .dp-backdrop-enter { opacity: 0; }
          .dp-backdrop-open  { opacity: 1; }

          .dp-modal {
            transition: opacity 250ms ease, transform 250ms ease;
          }
          .dp-modal-enter { opacity: 0; transform: scale(0.92) translateY(12px); }
          .dp-modal-open  { opacity: 1; transform: scale(1)    translateY(0);    }
        `}</style>

        {/* Backdrop */}
        <div
          className={`fixed inset-0 z-[9999998] dp-backdrop ${isOpen ? "dp-backdrop-open" : "dp-backdrop-enter"}`}
          style={{
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(2px)",
          }}
        />

        {/* Modal */}
        <div className="fixed inset-0 z-[9999999] flex items-center justify-center pointer-events-none">
          <div
            className={`dp-modal pointer-events-auto ${isOpen ? "dp-modal-open" : "dp-modal-enter"}`}
          >
            <div
              className="date-selection-wrapper shadow-2xl rounded-xl overflow-hidden"
              style={{ maxWidth: "96vw", maxHeight: "90vh", overflow: "auto" }}
            >
              <DateRangePicker
                onChange={handleDateChange}
                showPreview={true}
                dragSelectionEnabled={false}
                minDate={new Date(minDate)}
                maxDate={new Date(maxDate)}
                moveRangeOnFirstSelection={false}
                rangeColors={[PrimaryColor, PrimaryColor, PrimaryColor]}
                color={PrimaryColor}
                locale={enGB}
                months={2}
                ranges={
                  tempDateRange
                    ? [tempDateRange]
                    : [
                        {
                          startDate: dateRange.startDate || new Date(),
                          endDate: dateRange.endDate || new Date(),
                          key: "selection",
                        },
                      ]
                }
                direction="horizontal"
                disabledDates={disabledDates}
                inputRanges={[]}
              />
              <div className="flex justify-center gap-4 py-4 bg-white">
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 bg-[#F1935C] text-white rounded-lg"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>

        {showLongRangeConfirm && (
          <>
            <style>{`
              @keyframes dp-confirm-in {
                0%   { opacity: 0; transform: scale(0.96) translate(0, 6px); }
                55%  { opacity: 1; transform: scale(1) translate(0, 0); }
                70%  { transform: scale(1) translate(-4px, 0); }
                82%  { transform: scale(1) translate(3px, 0); }
                92%  { transform: scale(1) translate(-1.5px, 0); }
                100% { transform: scale(1) translate(0, 0); }
              }
              @keyframes dp-confirm-backdrop-in {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
              .dp-confirm-backdrop {
                animation: dp-confirm-backdrop-in 180ms ease-out both;
                background: rgba(15, 23, 42, 0.55);
                backdrop-filter: blur(4px);
              }
              .dp-confirm-card {
                animation: dp-confirm-in 650ms cubic-bezier(0.16, 1, 0.3, 1) both;
              }
              .dp-confirm-btn {
                transition: all 150ms ease;
              }
              .dp-confirm-btn:hover { transform: translateY(-1px); }
              .dp-confirm-btn:active { transform: translateY(0); }
            `}</style>
            <div
              className="fixed inset-0 z-[10000000] flex items-center justify-center px-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="dp-confirm-title"
            >
              <div
                className="fixed inset-0 dp-confirm-backdrop"
                onClick={handleCancelLongRange}
              />
              <div className="dp-confirm-card relative bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] w-full max-w-lg overflow-hidden">
                {(() => {
                  const dayCount = tempDateRange
                    ? Math.round(
                        (tempDateRange.endDate.getTime() -
                          tempDateRange.startDate.getTime()) /
                          (1000 * 60 * 60 * 24),
                      ) + 1
                    : 0;
                  const weekdayFmt = new Intl.DateTimeFormat("en-GB", {
                    weekday: "long",
                  });
                  const compactFmt = new Intl.DateTimeFormat("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  });
                  return (
                    <>
                      <div className="px-7 pt-7 pb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#F1935C"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          <span
                            className="text-[10px] font-semibold tracking-[0.14em]"
                            style={{ color: "#F1935C" }}
                          >
                            LONG RANGE
                          </span>
                        </div>
                        <h3
                          id="dp-confirm-title"
                          className="text-[17px] font-medium text-gray-600 leading-tight"
                        >
                          You're loading {dayCount} days of data
                        </h3>
                        <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                          Ranges longer than a month may take a while to fetch.
                          You can continue, or narrow the window for a faster
                          result.
                        </p>
                        {tempDateRange && (
                          <div
                            className="mt-5 px-4 py-3 rounded-xl flex items-center gap-3"
                            style={{ background: "rgba(241, 147, 92, 0.10)" }}
                          >
                            <div
                              className="flex-shrink-0 flex items-center justify-center rounded-lg bg-white"
                              style={{
                                width: 36,
                                height: 36,
                                boxShadow: "0 1px 2px rgba(241, 147, 92, 0.15)",
                              }}
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#F1935C"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="3"
                                  y="4"
                                  width="18"
                                  height="18"
                                  rx="2"
                                  ry="2"
                                />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[14px] font-medium text-gray-700">
                                {compactFmt.format(tempDateRange.startDate)}{" "}
                                <span
                                  style={{ color: "#F1935C" }}
                                  className="mx-1"
                                >
                                  →
                                </span>{" "}
                                {compactFmt.format(tempDateRange.endDate)}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {dayCount} days ·{" "}
                                {weekdayFmt.format(tempDateRange.startDate)} to{" "}
                                {weekdayFmt.format(tempDateRange.endDate)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2.5">
                        <button
                          onClick={handleCancelLongRange}
                          className="dp-confirm-btn px-5 py-2 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleConfirmLongRange}
                          autoFocus
                          className="dp-confirm-btn px-5 py-2 rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-1"
                          style={{
                            background: "#F1935C",
                            boxShadow:
                              "0 6px 16px -4px rgba(241, 147, 92, 0.55)",
                          }}
                        >
                          Continue anyway
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </>
        )}
      </>,
      document.body,
    );
  };

  const spanClassName = buttonClassName ? "" : "date-range";

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => (mounted ? closePicker() : openPicker())}
        className={buttonClassName || "date-range-button"}
        disabled={disabled}
        style={disabled ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
      >
        {defaultDates ? (
          <span className={spanClassName}>
            {singleDate
              ? formatDateWithOrdinal(dateRange!.startDate)
              : `${formatDateWithOrdinal(dateRange!.startDate)} - ${formatDateWithOrdinal(dateRange!.endDate)}`}
          </span>
        ) : (
          <span className={spanClassName}>
            {singleDate ? "Select Date" : "Add Date Range"}
          </span>
        )}
      </button>

      {renderDatePickerPortal()}
    </div>
  );
};
